import React, { useEffect, useState } from "react";
import styled, { keyframes } from "styled-components";
import axios from "axios";
import { message } from "antd";
import { useSearchParams } from "react-router-dom";
import TabelaPlanosCotas from "../../components/TabelaPlanosCotas";
import { validaCPF } from "../../components/Globais";
import { gerarCobrancaAssinaturaPublicaService } from "../../services/gerarCobrancaAssinaturaPublicaService";
import {
  hasNomeESobrenome,
  normalizeNomeCompleto,
} from "../../uteis/nomeCompleto";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Wrapper = styled.section`
  background-color: #ffffff;
  padding: 4rem 1.5rem;
  min-height: 100vh;

  @media (min-width: 768px) {
    padding: 5rem 4rem;
  }
`;

const FadeInContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  animation: ${fadeIn} 0.7s ease-out;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2.4rem;
  font-weight: 700;
  color: #0077cc;
  margin-bottom: 1rem;
`;

const Subtitle = styled.h2`
  font-size: 1.4rem;
  color: #222;
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const Paragraph = styled.p`
  font-size: 1.1rem;
  color: #555;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const List = styled.ul`
  text-align: left;
  max-width: 600px;
  margin: 0 auto 1.5rem;
  padding-left: 1rem;

  li {
    font-size: 1rem;
    color: #444;
    margin-bottom: 0.6rem;
  }
`;

const InfoBox = styled.div`
  background-color: #eef6ff;
  color: #003d73;
  padding: 1.5rem;
  border-radius: 8px;
  font-size: 1.05rem;
  margin: 2rem 0;
  text-align: center;
  font-weight: 500;
`;

const CTAButton = styled.button`
  background-color: #0077cc;
  color: white;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  border-radius: 6px;
  display: inline-block;
  transition: background 0.3s ease;
  cursor: pointer;

  &:hover:not(:disabled) {
    background-color: #005fa3;
  }

  &:disabled {
    background-color: #99c8eb;
    cursor: not-allowed;
  }
`;

const Small = styled.p`
  font-size: 0.9rem;
  color: #888;
  margin-top: 1rem;
`;

const FinalLine = styled.p`
  font-size: 1.1rem;
  color: #444;
  margin-top: 2rem;
  font-style: italic;
`;

type UltimoCadastroStorage = {
  nome?: string;
  telefone?: string;
  celular?: string;
  cpf?: string;
  quantidadeCotas?: number;
  pessoaId?: number;
};

const onlyDigits = (value?: string) => (value ?? "").replace(/\D/g, "");

const parseQuantidadeCotas = (value: unknown) => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string"
      ? Number(value)
      : NaN;

  if (!Number.isInteger(parsed) || parsed <= 0) {
    return null;
  }

  return parsed;
};

const normalizeCelular = (value?: string) => {
  const digits = onlyDigits(value);

  if (digits.startsWith("55") && digits.length > 11) {
    return digits.slice(2);
  }

  return digits;
};

const extractApiMessage = (payload: unknown) => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const message = (payload as { message?: unknown }).message;

  return typeof message === "string" && message.trim().length > 0
    ? message
    : null;
};

export default function ViewPagamentoVenda() {
  const [params] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const precoBase = 118.8;

  const descontos: Record<1 | 2 | 3 | 4 | 5 | 6, number> = {
    1: 0,
    2: 11.8,
    3: 28.3,
    4: 47.5,
    5: 71.3,
    6: 106.92,
  };

  const ultimoCadastro: UltimoCadastroStorage | null = (() => {
    const ultimoCadastro = localStorage.getItem("ultimoCadastro");

    if (!ultimoCadastro) {
      return null;
    }

    try {
      return JSON.parse(ultimoCadastro);
    } catch (error) {
      console.error("Erro ao ler ultimoCadastro:", error);
      return null;
    }
  })();

  const nome = normalizeNomeCompleto(ultimoCadastro?.nome);
  const celular = normalizeCelular(
    ultimoCadastro?.celular ?? ultimoCadastro?.telefone
  );
  const cpf = onlyDigits(ultimoCadastro?.cpf);
  const cotas =
    parseQuantidadeCotas(params.get("cotas")) ??
    parseQuantidadeCotas(ultimoCadastro?.quantidadeCotas);
  const pessoaId = ultimoCadastro?.pessoaId;
  const isQuantidadePermitida = cotas !== null && cotas <= 6;
  const desconto = cotas ? descontos[cotas as 1 | 2 | 3 | 4 | 5 | 6] || 0 : 0;
  const valorFinal = cotas ? precoBase * cotas - desconto : 0;
  const mensal = valorFinal / 12;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGerarCobranca = async () => {
    if (cotas === null) {
      message.error(
        "Não encontramos a quantidade de cotas do cadastro. Refaça o cadastro para continuar."
      );
      return;
    }

    if (cotas > 6) {
      message.error(
        "Para mais de 6 cotas, nossa equipe finaliza a ativação com atendimento personalizado."
      );
      return;
    }

    if (!hasNomeESobrenome(nome) || celular.length < 10 || !validaCPF(cpf)) {
      message.error(
        "Precisamos de nome completo, celular e CPF válidos para gerar a cobrança. Preencha seu cadastro novamente."
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await gerarCobrancaAssinaturaPublicaService({
        quantidadeCotas: cotas,
        pessoaId,
        nome,
        celular,
        cpf,
        formaPagamento: "CARTAO",
        planoAssinatura: "ANUNCIO_PROPRIEDADE",
        enviarWhatsapp: true,
      });

      const linkPagamento = response?.pagamento?.linkPagamento;

      if (!linkPagamento) {
        message.error(
          "A cobrança foi gerada, mas o link de pagamento não foi retornado."
        );
        return;
      }

      window.location.href = linkPagamento;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        const apiMessage = extractApiMessage(error.response?.data);

        if (status === 400) {
          message.error(
            apiMessage ??
              "Não foi possível gerar a cobrança: quantidade inválida ou valor não configurado."
          );
        } else if (status === 404) {
          message.error(
            "Não conseguimos gerar sua cobrança neste momento. Fale com nossa equipe e vamos te ajudar a concluir a ativação do anúncio."
          );
        } else if (status === 403) {
          message.error(
            "Seu acesso foi bloqueado temporariamente. Tente novamente mais tarde."
          );
        } else if (status === 429) {
          message.error(
            "Muitas tentativas em pouco tempo. Aguarde um pouco e tente novamente."
          );
        } else {
          message.error("Não foi possível gerar a cobrança agora.");
        }
      } else {
        message.error("Não foi possível gerar a cobrança agora.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Wrapper>
      <FadeInContent>
        <Title>Cadastro recebido com sucesso!</Title>

        <Paragraph>Recebemos as informações da sua cota.</Paragraph>

        <Paragraph>
          Vimos que você marcou a opção de <strong>venda</strong>, e isso é
          totalmente possível com a gente, com mais visibilidade, suporte e
          segurança.
        </Paragraph>

        <Subtitle>Como funciona a ativação do seu anúncio de venda:</Subtitle>

        <List>
          <li>Divulgação em nosso site e redes sociais</li>
          <li>
            Possibilidade de acompanhamento jurídico com escritório parceiro no
            fechamento da venda
          </li>
          <li>Suporte por WhatsApp e contato direto com interessados</li>
          <li>
            Opção de financiamento com financeira parceira, mais chances de
            fechar negócio
          </li>
          <li>Anúncio ativo por 12 meses</li>
        </List>

        <InfoBox>
          {isQuantidadePermitida ? (
            <>
              Você está ativando {cotas} cota{cotas > 1 ? "s" : ""} com
              suporte completo.
              <br />
              Valor total: <strong>R$ {valorFinal.toFixed(2)}</strong>
              <br />
              Equivalente a <strong>R$ {mensal.toFixed(2)}/mês</strong> por 12
              meses.
            </>
          ) : (
            <>
              Não foi possível confirmar a quantidade de cotas desta ativação.
              <br />
              Refaça o cadastro ou fale com nossa equipe para concluir o
              anúncio.
            </>
          )}
        </InfoBox>

        <Paragraph>
          Isso garante que só cotas reais estejam ativas e que os compradores
          recebam anúncios confiáveis.
        </Paragraph>

        <CTAButton
          type="button"
          onClick={handleGerarCobranca}
          disabled={isSubmitting || !isQuantidadePermitida}
        >
          {isSubmitting
            ? "Gerando cobrança..."
            : isQuantidadePermitida
            ? `Ativar anúncio de ${cotas} cota${cotas > 1 ? "s" : ""}`
            : "Ativação indisponível"}
        </CTAButton>

        <Small>
          Ao clicar, você será redirecionado para uma página segura de
          pagamento por cartão.
        </Small>

        <FinalLine>
          Você está a um passo de colocar sua cota em destaque e dar um novo
          destino a ela, com liberdade e visibilidade real.
        </FinalLine>

        <TabelaPlanosCotas />
      </FadeInContent>
    </Wrapper>
  );
}
